/*
 * usePageReturnRestore — put a page back at the offset Back left it at.
 *
 * Mounted once at the routing root beside ScrollToTop. On every route change
 * it asks pageReturn whether this arrival is a Back navigation with an
 * offset to restore; when it is, the central reset has already stood down
 * (useScrollResetOnRoute checks the same flag) and this settles the page at
 * the offset. The settle loop is the Battle Hub's (BattleHub.tsx): write the
 * offset each frame until the page reports it, because a page that fetches
 * or lazily renders is not tall enough to hold the offset on its first
 * frame, and the iOS WKWebView's native restoration can fight an early
 * single write. The budget is 3 s rather than the hub's 1.2 s: a page
 * arriving for the first time in a session also loads its route chunk, and
 * the Dex grid took 600 ms to grow past the offset even when warm (browser
 * pass, 2026-10-12). The first genuine user input disarms the loop at once,
 * so a long budget can never fight a scroll the trainer starts.
 *
 * A layout effect, with the first write made synchronously, pre-paint: the
 * same shape as the Dex's own restore (Pokedex.tsx). The page arrives
 * already parked where it was when it is tall enough on its first frame,
 * and the frames only converge the cases where it is not.
 */
import { useLayoutEffect } from "react";
import { useLocation } from "wouter";

import { takePageReturn } from "@/lib/pageReturn";
import { getPageScrollTop, scrollPageTo } from "@/lib/pageScroll";

const SETTLE_MS = 3000;
const DISARM_EVENTS = ["touchstart", "wheel", "keydown", "pointerdown"] as const;

export function usePageReturnRestore(): void {
  const [location] = useLocation();
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    const ret = takePageReturn(location);
    if (!ret || ret.scrollY <= 0) return;
    const started = Date.now();
    let raf = 0;
    let armed = true;
    const disarm = () => {
      armed = false;
    };
    for (const type of DISARM_EVENTS) {
      window.addEventListener(type, disarm, { capture: true, passive: true });
    }
    const settle = () => {
      if (!armed) return;
      scrollPageTo(ret.scrollY);
      if (Math.abs(getPageScrollTop() - ret.scrollY) > 2 && Date.now() - started < SETTLE_MS) {
        raf = requestAnimationFrame(settle);
      }
    };
    settle();
    return () => {
      cancelAnimationFrame(raf);
      for (const type of DISARM_EVENTS) {
        window.removeEventListener(type, disarm, { capture: true });
      }
    };
  }, [location]);
}
